import { useTranslation } from 'react-i18next';

/**
 * Translation object used to pass the key and values to the translation function.
 *
 * @property locKey - Translation key.
 * @property locVal - Translation values.
 */
export type TranslationObject = {
  locKey: string;
  locVal?: object;
};

/**
 * Custom hook that wraps the useTranslation hook from react-i18next and adds support for translation objects.
 * Translation objects are used to pass the key and values to the translation function.
 *
 * @example
 * ```tsx
 * const { t } = useObjectTranslation();
 * const message = t({ locKey: 'error.required', locVal: { field: 'Name' } });
 * ```
 *
 * @param args - Arguments passed to the useTranslation hook.
 */
export const useObjectTranslation = (
  ...args: Parameters<typeof useTranslation>
): ReturnType<typeof useTranslation> => {
  const { t: originalT, ...rest } = useTranslation(...args);

  const t: any = (...tArgs: Parameters<typeof originalT>) => {
    const translationObject = tArgs[0] as unknown as TranslationObject;
    if (!!translationObject && typeof translationObject === 'object') {
      return originalT(translationObject.locKey, translationObject.locVal);
    }

    return originalT(...tArgs);
  };

  return { t, ...rest };
};

export default useObjectTranslation;
